import { useMemo } from 'react';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import { getDevDocsFolderMetadata } from '../DevDocsFolderMetadata.ts';
import type { DevDocsContentPropertyNode } from '../DevDocsProperties.ts';

const MANAGED_FOLDER_SUBTYPE = 'managed';

export type DevDocsBylineConnectionAncestor = {
	readonly id?: string | null;
	readonly title?: string | null;
	readonly type?: string | null;
	readonly subType?: string | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- GraphQL content field, not browser history.
	readonly history?: {
		readonly createdBy?: {
			readonly displayName?: string | null;
		} | null;
	} | null;
	readonly properties?: {
		readonly nodes?: readonly DevDocsContentPropertyNode[] | null;
	} | null;
} | null;

export type DevDocsBylineConnection = {
	readonly folderId: string;
	readonly repoName: string;
	readonly provider: string | null;
	readonly owner: string | null;
	readonly lastSynced: string | null;
};

const isManagedFolderAncestor = (ancestor: DevDocsBylineConnectionAncestor): boolean =>
	ancestor?.type?.toLowerCase() === 'folder' &&
	ancestor?.subType?.toLowerCase() === MANAGED_FOLDER_SUBTYPE;

const getCreatedByDisplayName = (ancestor: DevDocsBylineConnectionAncestor): string | null => {
	const displayName = ancestor?.['history']?.createdBy?.displayName?.trim();
	return displayName || null;
};

export const getDevDocsConnectionFromAncestors = (
	ancestors?: readonly DevDocsBylineConnectionAncestor[] | null,
): DevDocsBylineConnection | null => {
	const managedDevDocsAncestor = ancestors?.find((ancestor) => {
		if (!isManagedFolderAncestor(ancestor)) {
			return false;
		}

		return getDevDocsFolderMetadata(ancestor?.properties?.nodes).hasDevDocsMetadata;
	});

	const folderMetadata = getDevDocsFolderMetadata(managedDevDocsAncestor?.properties?.nodes);
	const repoName = folderMetadata.devDocsProperties?.repoName;
	const folderId = managedDevDocsAncestor?.id;

	if (!repoName || !folderId) {
		return null;
	}

	return {
		folderId,
		repoName,
		provider: folderMetadata.containerApp,
		owner: getCreatedByDisplayName(managedDevDocsAncestor),
		lastSynced: folderMetadata.smartFolderProperties?.lastSynced ?? null,
	};
};

export const useDevDocsBylineConnection = (
	ancestors?: readonly DevDocsBylineConnectionAncestor[] | null,
): DevDocsBylineConnection | null => {
	const isDevDocsBylineConnectionEnabled = isExperimentEnabled('confluence_dev_docs');

	return useMemo(() => {
		if (!isDevDocsBylineConnectionEnabled) {
			return null;
		}

		return getDevDocsConnectionFromAncestors(ancestors);
	}, [ancestors, isDevDocsBylineConnectionEnabled]);
};
